/**
 * B2BCreateOrderForm — Formulaire de création de dossier B2B.
 *
 * Extrait de l'ex-`B2BTab.jsx` sans modification de comportement.
 * Gère le prefill clinique (mode/canal/échéance/commentaire), la pièce
 * jointe optionnelle, et la création (POST /api/b2b/orders).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, Loader2, FileText, X,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { GMPProductAutocomplete } from './GMPCatalog';
import { API, auth, CHANNEL_LABELS } from './B2BConstants';

export const CreateOrderForm = ({ meta, clinics, onCancel, onCreated }) => {
  // Délai par défaut : J+7
  const defaultDeadline = () => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  };
  const [form, setForm] = useState({
    clinic_name: clinics?.[0]?.name || '',
    clinic_id: clinics?.[0]?.id || null,
    requester_name: '',
    phone: '',
    channel: 'telephone',
    mode: 'retrait_pharmacie',
    comment: '',
    delivery_deadline: defaultDeadline(),
    total_amount: '',
    payment_due_date: '',
  });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);

  const updateItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems((arr) => [...arr, { designation: '', quantity: 1, code: '', unit: '', source: 'free_text' }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const onSelectClinic = (id) => {
    const c = clinics.find((x) => x.id === id);
    if (!c) {
      setForm((f) => ({ ...f, clinic_id: id }));
      return;
    }
    setForm((f) => {
      let nextPaymentDue = f.payment_due_date;
      if (!nextPaymentDue && Number.isInteger(c.payment_terms_days) && c.payment_terms_days > 0) {
        const d = new Date();
        d.setDate(d.getDate() + c.payment_terms_days);
        nextPaymentDue = d.toISOString().slice(0, 10);
      }
      return {
        ...f,
        clinic_id: id,
        clinic_name: c.name || f.clinic_name,
        requester_name: f.requester_name || c.contact_principal_name || '',
        phone: f.phone || c.phone_primary || '',
        mode: c.mode_default || f.mode,
        channel: c.channel_preferred || f.channel,
        payment_due_date: nextPaymentDue,
        comment: f.comment || c.special_conditions || '',
      };
    });
    if (c.contact_principal_name || c.phone_primary) {
      toast.info(`Préremplissage depuis la fiche « ${c.name} »`);
    }
  };

  const submit = async () => {
    if (!form.clinic_name?.trim()) { toast.error('Nom de clinique requis'); return; }
    const cleanItems = items.filter((it) => it.designation?.trim());
    const isPortail = form.channel === 'portail_b2b';
    if (isPortail && cleanItems.length === 0) {
      toast.error('Pour une commande via le portail B2B, ajoutez au moins un produit.'); return;
    }
    if (!isPortail && cleanItems.length === 0 && !form.comment?.trim() && !pendingAttachment) {
      toast.error('Ajoutez soit une pièce jointe, soit un commentaire, soit des articles.'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: cleanItems,
        total_amount: form.total_amount === '' ? null : parseFloat(form.total_amount),
        payment_due_date: form.payment_due_date || null,
      };
      const r = await axios.post(`${API}/b2b/orders`, payload, { headers: auth() });
      if (pendingAttachment) {
        try {
          const fd = new FormData();
          fd.append('file', pendingAttachment);
          await axios.post(`${API}/b2b/orders/${r.data.id}/attachment`, fd, {
            headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          toast.warning('Dossier créé mais pièce jointe non téléversée — réessayez depuis le détail.');
        }
      }
      toast.success(`Dossier créé · ${r.data.id.slice(0, 8)}`);
      onCreated?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de la création');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="b2b-create-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-medium text-zinc-600">Clinique *</Label>
          <select
            className="w-full h-10 px-3 mt-1 rounded-md border border-zinc-300 text-sm bg-white"
            value={form.clinic_id || ''}
            onChange={(e) => onSelectClinic(e.target.value)}
            data-testid="b2b-form-clinic"
          >
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Canal d&apos;entrée</Label>
          <select
            className="w-full h-10 px-3 mt-1 rounded-md border border-zinc-300 text-sm bg-white"
            value={form.channel}
            onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
            data-testid="b2b-form-channel"
          >
            {meta?.channels?.map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c] || c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Demandeur (contact clinique)</Label>
          <Input value={form.requester_name} onChange={(e) => setForm((f) => ({ ...f, requester_name: e.target.value }))} data-testid="b2b-form-requester" />
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Téléphone</Label>
          <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="b2b-form-phone" />
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Mode souhaité</Label>
          <select
            className="w-full h-10 px-3 mt-1 rounded-md border border-zinc-300 text-sm bg-white"
            value={form.mode}
            onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
            data-testid="b2b-form-mode"
          >
            <option value="retrait_pharmacie">Retrait pharmacie</option>
            <option value="livraison">Livraison</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Délai prévu (livraison/retrait)</Label>
          <Input
            type="date"
            value={form.delivery_deadline || ''}
            onChange={(e) => setForm((f) => ({ ...f, delivery_deadline: e.target.value }))}
            data-testid="b2b-form-deadline"
          />
          <p className="text-[10px] text-zinc-400 mt-1">Modifiable selon contexte (ex: commande internationale = délai long).</p>
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Montant total HT (FCFA) — optionnel à la création</Label>
          <Input
            type="number"
            min="0"
            step="100"
            placeholder="Ex : 850000"
            value={form.total_amount}
            onChange={(e) => setForm((f) => ({ ...f, total_amount: e.target.value }))}
            data-testid="b2b-form-amount"
          />
          <p className="text-[10px] text-zinc-400 mt-1">Sera renseigné au moment de la cotation si encore inconnu.</p>
        </div>
        <div>
          <Label className="text-xs font-medium text-zinc-600">Échéance paiement</Label>
          <Input
            type="date"
            value={form.payment_due_date}
            onChange={(e) => setForm((f) => ({ ...f, payment_due_date: e.target.value }))}
            data-testid="b2b-form-payment-due"
          />
          <p className="text-[10px] text-zinc-400 mt-1">Par défaut : J+30 après facturation (modifiable).</p>
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium text-zinc-600">Commentaire / résumé de la demande</Label>
        <Textarea
          value={form.comment}
          onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
          rows={2}
          placeholder="Ex : Urgent, commande prévue pour demain matin."
          data-testid="b2b-form-comment"
        />
      </div>

      <div data-testid="b2b-form-attachment-block">
        <Label className="text-xs font-medium text-zinc-600">📎 Joindre la demande du client</Label>
        <p className="text-[10px] text-zinc-500 mb-1.5">
          Capture WhatsApp, photo, PDF, scan du bon de commande — la saisie ligne par ligne n&apos;est pas obligatoire pour les demandes reçues hors portail.
        </p>
        {pendingAttachment ? (
          <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="b2b-form-attachment-preview">
            <FileText className="w-4 h-4 text-emerald-700" />
            <span className="text-sm flex-1 truncate">{pendingAttachment.name}</span>
            <span className="text-[10px] text-zinc-500">{Math.round(pendingAttachment.size / 1024)} Ko</span>
            <Button size="icon" variant="ghost" onClick={() => setPendingAttachment(null)} data-testid="b2b-form-attachment-clear">
              <X className="w-4 h-4 text-zinc-500" />
            </Button>
          </div>
        ) : (
          <label className="block">
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 8 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 8 Mo)'); return; }
                setPendingAttachment(f);
              }}
              className="text-xs"
              data-testid="b2b-form-attachment-input"
            />
            <p className="text-[10px] text-zinc-400 mt-1">PDF · JPG · PNG · HEIC (max 8 Mo)</p>
          </label>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold text-zinc-700">Produits / lignes <span className="text-[10px] text-zinc-400 font-normal">(optionnel hors portail B2B)</span></Label>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="b2b-form-add-item">
            <Plus className="w-4 h-4 mr-1" /> Ajouter une ligne
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="p-3 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg text-center" data-testid="b2b-items-empty">
            <p className="text-xs text-zinc-500 italic">
              Aucune ligne saisie. La cotation détaillée se fait dans Mediciel — joignez simplement la demande du client ci-dessus.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 p-2 bg-zinc-50 rounded-lg" data-testid={`b2b-item-row-${i}`}>
                <div className="col-span-6">
                  <GMPProductAutocomplete
                    value={it.designation}
                    onChange={(v) => updateItem(i, 'designation', v)}
                    onSelect={(prod) => {
                      setItems((arr) => arr.map((x, idx) => idx === i ? {
                        ...x,
                        designation: prod.name,
                        code: prod.code,
                        source: 'gmp_catalog',
                        unit_price: prod.price_vente_ttc || x.unit_price,
                      } : x));
                    }}
                    placeholder="Désignation (tape 2+ lettres pour catalogue) *"
                    data-testid={`b2b-item-designation-${i}`}
                  />
                </div>
                <Input className="col-span-2" placeholder="Quantité" type="number" min="0" step="0.5" value={it.quantity} onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)} />
                <Input className="col-span-2" placeholder="Unité (B, ml, ...)" value={it.unit || ''} onChange={(e) => updateItem(i, 'unit', e.target.value)} />
                <Input className="col-span-1" placeholder="Code" value={it.code || ''} onChange={(e) => updateItem(i, 'code', e.target.value)} />
                <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)} data-testid={`b2b-item-remove-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-zinc-400 mt-1">Ajoutez autant de lignes que nécessaire. Les produits hors catalogue se saisissent librement.</p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
        <Button variant="outline" onClick={onCancel} data-testid="b2b-form-cancel">Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="b2b-form-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Créer le dossier
        </Button>
      </div>
    </div>
  );
};
